// ============================================================
// WHERE A PLATE'S CALLOUTS STAND: THE FREE SPOT (plan plate-unboxed; the
// user's ruling "every body should fill the tab and be about the same
// size", 2026-10-13). Replaces the wide and tight columns of design-gaps P2.
//
// Each callout takes the nearest spot free of the drawing itself - beside
// the head, between the legs, under a hand - preferring the figure's own
// width, so the frame round everything can fit tight and the figure grows
// to fill its cell. A vehicle's facing labels are placed by the same
// search, each on its own side. Nothing sits on the drawing or on another
// word, and the frame holds the plate's words above and its status below.
//
// The drawing is the art's own geometry (its contour rings, or its region
// pieces), filled into a grid of 12-unit cells and thickened three cells. `k`
// shrinks the words (never grows them) so they keep one size on screen
// however large the figure is drawn; the caller picks it from the cell.
//
// The search's constants are the mock's, measured on the filmed reference
// "The Plate, As Ruled": rows 40 apart up to 14 either way at 1.4 the cost
// of a step across, steps of 20 from 40 off the anchor, 18 of padding, a
// 70 berth between callouts side by side, 500 for leaving the figure's
// width; facing labels from 30 off the drawing.
//
// Pure. Zero React dependency. Frame units (a figure is 2123 x 3000, y down).
// ============================================================

import type { Box, Point } from './frame';
import type { Frame } from './calloutLayout';
import { PLATE_TYPE } from './plateType';

/** What a plate draws, for the search: its rings (filled, even-odd each) and its bounds [x0, y0, x1, y1]. */
export interface PlacementShape {
  rings: readonly (readonly Point[])[];
  bounds: Box;
}

/** A callout to place: its side, the dot on its part, and its words' reach and band about its row, unscaled. */
export interface PlaceCallout {
  id: string;
  side: 'L' | 'R';
  anchor: Point;
  width: number;
  above: number;
  below: number;
}

/** A facing label to place: where it would stand, how it anchors, and its box about that point, unscaled. */
export interface PlaceLabel {
  id: string;
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  width: number;
  above: number;
  below: number;
}

/** Where everything stands: each callout's column and row, each label's point, the frame, and the words' scale. */
export interface Placement {
  frame: Frame;
  /** A callout's column (its text starts here on the right, ends here on the left) and its row. */
  at: Record<string, { x: number; row: number }>;
  labels: Record<string, { x: number; y: number }>;
  /** The callouts' scale about their column and row; a label's is never above 1. */
  k: number;
}

const CELL = 12;
const PAD = 18;
const BERTH = 70;
const ROW_STEP = 40;
const ROWS = 14;
const ROW_COST = 1.4;
const X_STEP = 20;
const START = 40;
const OUTSIDE = 500;
const LABEL_START = 30;
const MARGIN = 40;
const REACH = 600;

/** A box as [x0, y0, x1, y1]. */
type Rect = [number, number, number, number];

const meet = (a: Rect, b: Rect) => a[0] < b[2] && b[0] < a[2] && a[1] < b[3] && b[1] < a[3];
const grow = (r: Rect, x: number, y: number): Rect => [r[0] - x, r[1] - y, r[2] + x, r[3] + y];

/** The drawing as a grid of cells, true where it draws. */
interface Grid {
  x0: number;
  y0: number;
  cols: number;
  rows: number;
  cells: Uint8Array;
  /**
   * The cells' running sums (a summed-area table, (cols + 1) x (rows + 1)): how many drawn cells lie above and left
   * of each corner, so whether a box meets the drawing is four lookups, not a scan of every cell under it.
   */
  sums: Int32Array;
  /** All the shape draws, in frame units (the grid keeps four cells round it for the spread). */
  extent: Box;
}

/** Where a ring's edges cross the line y, sorted: the even-odd spans between them are inside. */
function crossings(ring: readonly Point[], y: number): number[] {
  const xs: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[(i + 1) % ring.length];
    if (ay <= y !== by <= y) xs.push(ax + ((y - ay) / (by - ay)) * (bx - ax));
  }
  return xs.sort((a, b) => a - b);
}

/** Fill one ring into the grid, row by row through each cell's middle. */
function fillRing(grid: Grid, ring: readonly Point[]) {
  for (let r = 0; r < grid.rows; r++) {
    const xs = crossings(ring, grid.y0 + (r + 0.5) * CELL);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const c0 = Math.max(0, Math.floor((xs[i] - grid.x0) / CELL));
      const c1 = Math.min(grid.cols - 1, Math.floor((xs[i + 1] - grid.x0) / CELL));
      grid.cells.fill(1, r * grid.cols + c0, r * grid.cols + c1 + 1);
    }
  }
}

/**
 * The cells round a drawn cell that count as drawn: three either way (36 units). A contour runs inside the drawn
 * line's stroke and its glow, and the mock kept words 35 units off the lines thickened (plus the padding).
 */
const SPREAD = [-3, -2, -1, 0, 1, 2, 3];
const AROUND = SPREAD.flatMap((dr) => SPREAD.map((dc) => [dr, dc] as const));

/** Mark a cell and every cell AROUND it, where they lie in the grid. */
function markAround({ cols, rows }: Grid, out: Uint8Array, i: number) {
  const r = Math.floor(i / cols);
  const c = i % cols;
  for (const [dr, dc] of AROUND) {
    const rr = r + dr;
    const cc = c + dc;
    if (rr >= 0 && rr < rows && cc >= 0 && cc < cols) out[rr * cols + cc] = 1;
  }
}

/** The grid thickened (AROUND), so a word never touches a line or its glow. */
function thicken(grid: Grid): Grid {
  const out = new Uint8Array(grid.cells.length);
  grid.cells.forEach((on, i) => on && markAround(grid, out, i));
  return { ...grid, cells: out };
}

/** Everything the shape draws: its bounds and every ring's points (a contour may reach past the art's box). */
function extentOf({ rings, bounds }: PlacementShape): Box {
  const xs = [bounds[0], bounds[2], ...rings.flatMap((r) => r.map((p) => p[0]))];
  const ys = [bounds[1], bounds[3], ...rings.flatMap((r) => r.map((p) => p[1]))];
  return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
}

/** What `map` holds for `key`, made (and kept) the first time it is asked for. */
export function cachedOn<K extends object, V>(map: WeakMap<K, V>, key: K, make: () => V): V {
  if (!map.has(key)) map.set(key, make());
  return map.get(key)!;
}

/** Each shape's grid, made once: a layout tries several word scales over the same drawing. */
const grids = new WeakMap<PlacementShape, Grid>();

/** The shape as a grid over all it draws (made once per shape): its rings filled, or - with none - its bounds solid. */
export const occupancy = (shape: PlacementShape): Grid => cachedOn(grids, shape, () => gridOf(shape));

function gridOf(shape: PlacementShape): Grid {
  const extent = extentOf(shape);
  const [x0, y0, x1, y1] = extent;
  const cols = Math.ceil((x1 - x0) / CELL) + 8;
  const rows = Math.ceil((y1 - y0) / CELL) + 8;
  const cells = new Uint8Array(cols * rows);
  const grid: Grid = { x0: x0 - 4 * CELL, y0: y0 - 4 * CELL, cols, rows, cells, sums: new Int32Array(0), extent };
  if (shape.rings.length === 0) grid.cells.fill(1);
  for (const ring of shape.rings) fillRing(grid, ring);
  const thick = thicken(grid);
  return { ...thick, sums: summed(thick) };
}

/** The grid's summed-area table: sums[(r + 1) * (cols + 1) + c + 1] counts the drawn cells in rows 0..r, cols 0..c. */
function summed({ cols, rows, cells }: Pick<Grid, 'cols' | 'rows' | 'cells'>): Int32Array {
  const w = cols + 1;
  const sums = new Int32Array(w * (rows + 1));
  for (let r = 0; r < rows; r++) {
    let row = 0;
    for (let c = 0; c < cols; c++) {
      row += cells[r * cols + c];
      sums[(r + 1) * w + c + 1] = sums[r * w + c + 1] + row;
    }
  }
  return sums;
}

/** Whether a box lies on any drawn cell (outside the grid nothing is drawn). */
function onDrawing(grid: Grid, [bx0, by0, bx1, by1]: Rect): boolean {
  const c0 = Math.max(0, Math.floor((bx0 - grid.x0) / CELL));
  const c1 = Math.min(grid.cols - 1, Math.floor((bx1 - grid.x0) / CELL));
  const r0 = Math.max(0, Math.floor((by0 - grid.y0) / CELL));
  const r1 = Math.min(grid.rows - 1, Math.floor((by1 - grid.y0) / CELL));
  if (c1 < c0 || r1 < r0) return false;
  const w = grid.cols + 1;
  const at = (r: number, c: number) => grid.sums[r * w + c];
  return at(r1 + 1, c1 + 1) - at(r0, c1 + 1) - at(r1 + 1, c0) + at(r0, c0) > 0;
}

/** The rows a search tries, nearest first: the anchor's, then 40 above and below, out to 14 either way. */
const rowOffsets = () => [
  0,
  ...Array.from({ length: ROWS }, (_, i) => [(i + 1) * ROW_STEP, -(i + 1) * ROW_STEP]).flat(),
];

/** What a placement must keep clear of: the drawing, the boxes already placed (and their berth), the bands. */
interface Clear {
  grid: Grid;
  placed: Rect[];
  bands: readonly Rect[];
}

const isFree = ({ grid, placed, bands }: Clear, box: Rect) => {
  const padded = grow(box, PAD, PAD);
  if (onDrawing(grid, padded) || bands.some((b) => meet(b, padded))) return false;
  return !placed.some((q) => meet(grow(q, BERTH, 0), padded));
};

/** A callout's box at a column and row, scaled by k about them. */
function calloutBox(c: PlaceCallout, x: number, row: number, k: number): Rect {
  const w = c.width * k;
  return [c.side === 'R' ? x : x - w, row - c.above * k, c.side === 'R' ? x + w : x, row + c.below * k];
}

/** A box within the drawing's width (a little either way), where a callout is preferred. */
const within = (box: Rect, [x0, , x1]: Box) => box[0] >= x0 - 10 && box[2] <= x1 + 10;

/** A spot's cost: the way across, the way along (dearer), and leaving the drawing's width. */
const costOf = (c: PlaceCallout, box: Rect, row: number, d: number, bounds: Box) =>
  d + Math.abs(row - c.anchor[1]) * ROW_COST + (within(box, bounds) ? 0 : OUTSIDE);

/** The nearest free column along one row for a callout, and its cost; null when the row has none in reach. */
function alongRow(c: PlaceCallout, row: number, k: number, clear: Clear) {
  const bounds = clear.grid.extent;
  for (let d = START; d < bounds[2] - bounds[0] + REACH; d += X_STEP) {
    const x = c.side === 'R' ? c.anchor[0] + d : c.anchor[0] - d;
    const box = calloutBox(c, x, row, k);
    if (isFree(clear, box)) return { x, row, box, cost: costOf(c, box, row, d, bounds) };
  }
  return null;
}

/** A callout's best spot over every row tried; beside the drawing's edge on its row if none is free. */
function spotFor(c: PlaceCallout, k: number, clear: Clear) {
  let best: ReturnType<typeof alongRow> = null;
  for (const dy of rowOffsets()) {
    const spot = alongRow(c, c.anchor[1] + dy, k, clear);
    if (spot && (!best || spot.cost < best.cost)) best = spot;
  }
  if (best) return best;
  const [x0, , x1] = clear.grid.extent;
  const x = c.side === 'R' ? x1 + BERTH : x0 - BERTH;
  return { x, row: c.anchor[1], box: calloutBox(c, x, c.anchor[1], k), cost: Infinity };
}

/** How far a label's anchor stands from its box's left edge. */
const anchorOffset = (l: PlaceLabel, w: number) => (l.anchor === 'start' ? 0 : l.anchor === 'end' ? w : w / 2);

/** A label's box when its anchor point stands at (x, y), scaled by k about that point. */
function labelBox(l: PlaceLabel, x: number, y: number, k: number): Rect {
  const w = l.width * k;
  const left = x - anchorOffset(l, w);
  return [left, y - l.above * k, left + w, y + l.below * k];
}

/** Which side of the drawing a label belongs to: beside it (left or right), or above or below it. */
interface LabelSide {
  sideways: boolean;
  before: boolean;
  bounds: Box;
}

function sideOf(l: PlaceLabel, bounds: Box): LabelSide {
  const [x0, y0, x1, y1] = bounds;
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  const sideways = Math.abs(l.x - mx) / (x1 - x0) > Math.abs(l.y - my) / (y1 - y0);
  return { sideways, before: sideways ? l.x < mx : l.y < my, bounds };
}

/** The near edge `d` off the drawing on the label's side. */
function edgeAt({ sideways, before, bounds: [x0, y0, x1, y1] }: LabelSide, d: number) {
  if (sideways) return before ? x0 - d : x1 + d;
  return before ? y0 - d : y1 + d;
}

/** The anchor point putting a label's box at `edge` on its side, its row (or column) shifted by `shift`. */
function labelAt(l: PlaceLabel, side: LabelSide, { edge, shift }: { edge: number; shift: number }, k: number): Point {
  const w = l.width * k;
  if (side.sideways) return [(side.before ? edge - w : edge) + anchorOffset(l, w), l.y + shift];
  return [l.x + shift, side.before ? edge - l.below * k : edge + l.above * k];
}

/** The first free place outward from the drawing along one row (or column) shift, and its cost. */
function firstFree(l: PlaceLabel, side: LabelSide, shift: number, k: number, clear: Clear) {
  for (let d = LABEL_START; d < REACH * 2; d += X_STEP) {
    const at = labelAt(l, side, { edge: edgeAt(side, d), shift }, k);
    const box = labelBox(l, at[0], at[1], k);
    if (isFree(clear, box)) return { at, box, cost: d + Math.abs(shift) * ROW_COST };
  }
  return null;
}

/** A facing label's best place on its own side of the drawing: as near it as the space allows. */
function labelSpot(l: PlaceLabel, k: number, clear: Clear) {
  const side = sideOf(l, clear.grid.extent);
  let best: ReturnType<typeof firstFree> = null;
  for (const shift of rowOffsets()) {
    const spot = firstFree(l, side, shift, k, clear);
    if (spot && (!best || spot.cost < best.cost)) best = spot;
  }
  return best ?? { at: [l.x, l.y] as Point, box: labelBox(l, l.x, l.y, k), cost: Infinity };
}

/**
 * The frame round the drawing, every placed box and the bands' heights, with a margin - as wide either side of the
 * drawing's middle (`mid`), so the figure stands in the centre of its panel even when its callouts reach further on
 * one side (the user, 2026-10-14: the daemon stood left of centre).
 */
function frameOf(bounds: Box, boxes: readonly Rect[], bands: readonly Rect[], mid: number): Frame {
  const all: Rect[] = [[...bounds] as Rect, ...boxes, ...bands.map((b): Rect => [bounds[0], b[1], bounds[2], b[3]])];
  const half = Math.max(mid - Math.min(...all.map((b) => b[0])), Math.max(...all.map((b) => b[2])) - mid) + MARGIN;
  const y0 = Math.min(...all.map((b) => b[1])) - MARGIN;
  const y1 = Math.max(...all.map((b) => b[3])) + MARGIN;
  return { x: mid - half, y: y0, w: 2 * half, h: y1 - y0 };
}

/**
 * Place every callout, then every facing label, each in the nearest spot free of the drawing and of what is already
 * placed, and fit the frame round all of it. `bands` are heights the frame holds and nothing may sit in (the
 * plate's words above the figure, its status below), as [y0, y1] across the drawing's width and beyond.
 */
export function placeCallouts(
  shape: PlacementShape,
  callouts: readonly PlaceCallout[],
  labels: readonly PlaceLabel[],
  { k, bands }: { k: number; bands: readonly (readonly [number, number])[] },
): Placement {
  const grid = occupancy(shape);
  const [bx0, , bx1] = grid.extent;
  const bandRects = bands.map(([y0, y1]): Rect => [bx0 - REACH * 3, y0, bx1 + REACH * 3, y1]);
  const clear: Clear = { grid, placed: [], bands: bandRects };
  const at: Placement['at'] = {};
  for (const c of callouts) {
    const spot = spotFor(c, k, clear);
    at[c.id] = { x: spot.x, row: spot.row };
    clear.placed.push(spot.box);
  }
  const placedLabels: Placement['labels'] = {};
  for (const l of labels) {
    const spot = labelSpot(l, k, clear);
    placedLabels[l.id] = { x: spot.at[0], y: spot.at[1] };
    clear.placed.push(spot.box);
  }
  const mid = (shape.bounds[0] + shape.bounds[2]) / 2;
  return { frame: frameOf(grid.extent, clear.placed, bandRects, mid), at, labels: placedLabels, k };
}

/**
 * The words' scale for a frame drawn in a cell: the tallest word (`textUnits` high in frame units) held to `px` on
 * screen, never grown past its own size; 1 with no cell yet. Rounded to twentieths, so a resize that changes nothing
 * on screen changes nothing here.
 */
export function wordsScale(frame: Frame, cell: { w: number; h: number } | null, textUnits: number, px = 24): number {
  if (!cell || cell.w <= 0 || cell.h <= 0) return 1;
  const s = Math.min(cell.w / frame.w, cell.h / frame.h);
  return Math.min(1, Math.round((px / (textUnits * s)) * 20) / 20);
}

/** The words' scales tried for a cell, full size down: the plate never grows its words, it may shrink them this far. */
const SCALES = [1, 0.9, 0.8, 0.7, 0.6];

/** The smallest word a callout draws (its smallest type: a tag's), and the least it may be on screen, in px. */
const SMALLEST = Math.min(...Object.values(PLATE_TYPE).map((t) => t.px));
const LEGIBLE = 8;

/** How many inputs a shape keeps placed: the target's and the actor's plate may draw one figure at once. */
const KEPT = 8;
/** Each shape's placements at every scale, by their inputs (none of them reads the cell). */
const placed = new WeakMap<PlacementShape, Map<string, Placement[]>>();

/**
 * The placement at each of the words' scales, made once per shape and inputs: a resize only picks among them, and
 * an update that leaves the callouts as they were (a wound, a sync echo) places nothing.
 */
function placementsOf(
  shape: PlacementShape,
  callouts: readonly PlaceCallout[],
  labels: readonly PlaceLabel[],
  bands: readonly (readonly [number, number])[],
): Placement[] {
  const key = JSON.stringify([callouts, labels, bands]);
  const byKey = cachedOn(placed, shape, () => new Map<string, Placement[]>());
  const made = byKey.get(key) ?? SCALES.map((k) => placeCallouts(shape, callouts, labels, { k, bands }));
  byKey.delete(key);
  byKey.set(key, made);
  if (byKey.size > KEPT) byKey.delete(byKey.keys().next().value!);
  return made;
}

/** One scale tried: its placement, how large it draws the figure, and whether its words are the right size. */
interface Tried {
  placement: Placement;
  scale: number;
  fits: boolean;
  legible: boolean;
}

/** The best of the scales tried: the largest figure whose words fit and stay legible; else the most legible fit. */
function bestOf(tried: readonly Tried[]): Placement | null {
  const fit = tried.filter((t) => t.fits);
  const legible = fit.filter((t) => t.legible);
  if (legible.length) return legible.reduce((a, b) => (b.scale > a.scale ? b : a)).placement;
  if (!fit.length) return null;
  return fit.reduce((a, b) => (b.placement.k * b.scale > a.placement.k * a.scale ? b : a)).placement;
}

/**
 * The placement for a cell: of the words' scales tried, the one that draws the figure largest while its words stay
 * near one size on screen (`wordsScale` allows at least that scale for the frame it gives). Smaller words free room
 * beside the parts, the frame shrinks and the figure grows, so the search is over the scale, not a settling loop
 * (which stopped at full size on a lanky figure: a wide frame draws small and allows big words). No word goes below
 * 8 px on screen. With no cell yet, full size.
 */
export function placeForCell(
  shape: PlacementShape,
  callouts: readonly PlaceCallout[],
  labels: readonly PlaceLabel[],
  {
    bands,
    cell,
    textUnits,
  }: { bands: readonly (readonly [number, number])[]; cell: { w: number; h: number } | null; textUnits: number },
): Placement {
  const placements = placementsOf(shape, callouts, labels, bands);
  if (!cell) return placements[0];
  const tried = placements.map((placement): Tried => {
    const { k } = placement;
    const scale = Math.min(cell.w / placement.frame.w, cell.h / placement.frame.h);
    const fits = wordsScale(placement.frame, cell, textUnits) >= k;
    return { placement, scale, fits, legible: SMALLEST * k * scale >= LEGIBLE };
  });
  return bestOf(tried) ?? tried[tried.length - 1].placement;
}
